import { ErrorHandler, NgModule, Optional, SkipSelf, PLATFORM_ID, inject } from '@angular/core';
import {
    APP_BASE_HREF,
    DatePipe,
    Location,
    LocationStrategy,
    PathLocationStrategy,
    PlatformLocation,
    registerLocaleData,
    isPlatformBrowser
} from "@angular/common";
import { provideHttpClient, withFetch, withInterceptorsFromDi } from "@angular/common/http";
import { NgxMaskDirective, NgxMaskOptions, NgxMaskPipe, provideNgxMask } from "ngx-mask";
import {
    DEFAULT_LOCALE_VALUE,
    DEFAULT_LOCALES,
    DEFAULT_REGION_VALUE,
    LOCAL_STORAGE_PROVIDER,
    USE_DEFAULT_REGIONS,
    WINDOW_PROVIDER
} from "@biletix/core/common";
import { HTTP_INTERCEPTORS_PROVIDERS } from "@biletix/core/interceptors/interceptors.provider";

import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader, TranslateHttpLoaderConfig } from "@ngx-translate/http-loader";
import { Observable, of } from 'rxjs';
import { EventService } from "@biletix/core/services/event.service";
import { EventPackageGroupService } from "@biletix/core/services/event-package-group.service";
import { ConsoleLogger } from "@biletix/core/logging/console.logger";
import { NotificationService } from "@biletix/core/services/notification.service";
import { LocalStorageService } from "@biletix/core/services/local-storage.service";
import { I18nService } from "@biletix/core/services/i18n.service";
import { GlobalErrorHandler } from "@biletix/core/services/global-error-handler.service";
import { LanguageGuard } from "@biletix/core/guards/language.guard";
import { LOADING_CONFIG_DEFAULT, LoadingScreenService } from "@biletix/core/components/loading/loading-screen.service";
import { OverlayService } from "@biletix/core/services/overlay.service";
import { AuthenticationGuard } from "@biletix/core/guards/authentication.guard";
import { CustomerAccountService } from "@biletix/core/services/customer-account.service";
import { ApplicationStateService } from "@biletix/core/services/application-state.service";
import { AuthenticationService } from "@biletix/core/services/authentication.service";
import { TicketSaleService } from "@biletix/core/services/ticket-sale.service";
import { CreditCardTypeService } from "@biletix/core/services/credit-card-type.service";
import { RegionGuard } from "@biletix/core/guards/region.guard";
import { CustomPaginatorIntl } from "@biletix/core/configurations/custom-paginator-intl";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { RegionService } from "@biletix/core/services/region.service";
import { DeliveryService } from "@biletix/core/services/delivery.service";
import { AnonymousGuard } from "@biletix/core/guards/anonymous.guard";
import { CustomerService } from "@biletix/core/services/customer.service";
import { CustomerVerifyService } from "@biletix/core/services/customer.verify.service";
import { ShoppingCartResolver } from "@biletix/core/resolvers/shopping-cart.resolver";
import { ShoppingCartGuard } from "@biletix/core/guards/shopping-cart.guard";
import { LoggedInCustomerResolver } from "@biletix/core/resolvers/logged-in-customer.resolver";
import { NavigationService } from "@biletix/core/services/navigation.service";
import {
    MAT_MOMENT_DATE_ADAPTER_OPTIONS,
    MAT_MOMENT_DATE_FORMATS,
    MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { RouterService } from "@biletix/core/services/router.service";

import tr from '@angular/common/locales/tr';
import en from '@angular/common/locales/en';
import {COUNTDOWN_CONFIG, CountdownComponent, CountdownConfig} from "ngx-countdown";
import {MaterialModule} from "@biletix/core/material/material.module";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MainLayoutComponent} from "@biletix/core/layouts/main-layout/main-layout.component";
import {HeaderComponent} from "@biletix/core/layouts/main-layout/header/header.component";
import {FooterComponent} from "@biletix/core/layouts/main-layout/footer/footer.component";
import {LoadingComponent} from "@biletix/core/components/loading/loading.component";
import {AccountDropdownComponent} from "@biletix/core/layouts/main-layout/header/account-dropdown/account-dropdown.component";
import {CartDropdownComponent} from "@biletix/core/layouts/main-layout/header/cart-dropdown/cart-dropdown.component";
import {EventSearchBoxComponent} from "@biletix/core/layouts/main-layout/header/event-search-box/event-search-box.component";
import {EventCategoryMenuComponent} from "@biletix/core/layouts/main-layout/header/event-category-menu/event-category-menu.component";
import {EventCategoryMobileMenuComponent} from "@biletix/core/layouts/main-layout/header/event-category-mobile-menu/event-category-mobile-menu.component";
import {TopBandComponent} from "@biletix/core/layouts/main-layout/header/top-band/top-band.component";
import {EventSearchNotFoundComponent} from "@biletix/core/layouts/main-layout/header/event-search-box/event-search-not-found/event-search-not-found.component";
import {SecondaryLayoutComponent} from "@biletix/core/layouts/secondary-layout/secondary-layout.component";
import {SecondaryEmptyHeaderComponent} from "@biletix/core/layouts/secondary-layout/empty-header/empty-header.component";
import {ThirdEmptyHeaderComponent} from "@biletix/core/layouts/third-layout/empty-header/empty-header.component";
import {SecondaryEmptyFooterComponent} from "@biletix/core/layouts/secondary-layout/empty-footer/empty-footer.component";
import {ThirdEmptyFooterComponent} from "@biletix/core/layouts/third-layout/empty-footer/empty-footer.component";
import {AuthLayoutComponent} from "@biletix/core/layouts/auth-layout/auth-layout.component";
import {BrowserModule, Title} from "@angular/platform-browser";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {RouterModule} from "@angular/router";
import {SiteLogoComponent} from './layouts/site-logo/site-logo.component';
import {SideNavComponent} from './layouts/side-nav/side-nav.component';
import {FooterToggleDirective} from './layouts/footer-toggle.directive';
import {GlobalParameters} from "@biletix/core/common/constants/global-parameters";
import {CartTimeoutService} from "@biletix/core/services/cart-timeout.service";
import {CartResetTimeoutResolver} from "@biletix/core/resolvers/cart-reset-timeout.resolver";
import {TranslateModuleConfig} from "@ngx-translate/core";
import {SetLoadingConfigGuard} from "@biletix/core/components/loading/set-loading-config.guard";
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { HeaderCountdownTimerComponent } from './components/header-countdown-timer/header-countdown-timer.component';
import { PerformancePageStateService } from "@biletix/features/performance/services/performance-page-state.service";
import { QRCodeComponent } from 'angularx-qrcode';
import { StatusCodeResponseService } from './services/status-code-response.service';
import { environment } from '@biletix/env';
import { EventAnonimUserProtectGuard } from './guards/event-anonim-user-protect-guard';
import { TFFFanService } from '@biletix/features/account/components/account-page/tff/fan/services/tff-fan-service';
import { ThirdLayoutComponent } from "@biletix/core/layouts/third-layout/third-layout.component";
import {
    TFFStructureService
} from "@biletix/features/account/components/account-page/tff/fan/services/tff-structure-service";
import {
    TffConstantService
} from "@biletix/features/account/components/account-page/tff/fan/services/tff-constant-service";
import { TffMatchService } from "@biletix/features/account/components/account-page/tff/fan/services/tff-match-service";
import { WideLayoutComponent } from './layouts/wide-layout/wide-layout.component';
import { WideHeaderComponent } from './layouts/wide-layout/header/header.component';
import { WideTopBandComponent } from './layouts/wide-layout/header/top-band/top-band.component';
import { WideEventCategoryMenuComponent } from './layouts/wide-layout/header/event-category-menu/event-category-menu.component';
import { WideEventCategoryMobileMenuComponent } from './layouts/wide-layout/header/event-category-mobile-menu/event-category-mobile-menu.component';
import { WideEventSearchBoxComponent } from './layouts/wide-layout/header/event-search-box/event-search-box.component';
import { WideEventSearchNotFoundComponent } from './layouts/wide-layout/header/event-search-box/event-search-not-found/event-search-not-found.component';
import { WideAccountDropdownComponent } from './layouts/wide-layout/header/account-dropdown/account-dropdown.component';
import { WideRegionSelectorComponent } from './layouts/wide-layout/header/region-selector/region-selector.component';
import { WideCartDropdownComponent } from './layouts/wide-layout/header/cart-dropdown/cart-dropdown.component';
import { WideFooterComponent } from './layouts/wide-layout/footer/footer.component';
import { BtxDateRangePickerDialogComponent } from './components/date-range-picker-dialog/date-range-picker-dialog.component';
import {FanRegisterResolver} from "@biletix/core/resolvers/fan-register.resolver";
import {TFFTicketService} from "@biletix/features/account/components/account-page/tff/fan/services/tff-ticket-service";
import {ReservationTicketSaleService} from "@biletix/core/services/reservation-ticket-sale.service";
import { FootballService } from '@biletix/core/services/football.service';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { FacebookPixelService } from '@biletix/core/services/facebook-pixel.service';

registerLocaleData(en);
registerLocaleData(tr);

//
const maskConfig: NgxMaskOptions = {
    validation: true,
};

const baseHref = environment.baseHref ?? '/';
const normalizedBaseHref = baseHref.endsWith('/') ? baseHref : `${baseHref}/`;
const translateHttpLoaderConfig: Partial<TranslateHttpLoaderConfig> = {
    prefix: `${normalizedBaseHref}assets/i18n/`,
    suffix: `.json?v=${environment.JENKINS_BUILD_NUMBER}`,
};

// The files only change with a deploy (which restarts the process), so each one is read from disk once
// per process. The raw text is cached, not the parsed object: every render gets its own copy and
// nothing a request does to it can leak into the next one.
const serverTranslationContent = new Map<string, string>();

class ServerSafeTranslateLoader implements TranslateLoader {
    getTranslation(lang: string): Observable<any> {
        const cached = serverTranslationContent.get(lang);
        if (cached !== undefined) {
            return of(JSON.parse(cached));
        }
        try {
            // Use dynamic import for fs module (Node.js only)
            const fs = require('fs');
            const path = require('path');

            // Try multiple paths as the dist structure may vary
            const cwd = process.cwd();
            const possiblePaths = [
                path.join(cwd, 'dist', 'biletix-web-client', 'browser', 'assets', 'i18n', `${lang}.json`),
                path.join(cwd, 'src', 'assets', 'i18n', `${lang}.json`),
                path.join(cwd, 'public', 'assets', 'i18n', `${lang}.json`),
                path.join(cwd, 'assets', 'i18n', `${lang}.json`),
            ];

            for (const filePath of possiblePaths) {
                if (fs.existsSync(filePath)) {
                    const content = fs.readFileSync(filePath, 'utf-8');
                    const parsed = JSON.parse(content);
                    serverTranslationContent.set(lang, content);
                    console.log(`Successfully loaded translations from: ${filePath}`);
                    return of(parsed);
                }
            }

            console.warn(`Translation file not found for language: ${lang}. Tried paths:`, possiblePaths);
            return of({});
        } catch (error) {
            console.error('Error loading translations on server:', error);
            return of({});
        }
    }
}

export function createTranslateLoader(platformId: Object): TranslateLoader {
    if (isPlatformBrowser(platformId)) {
        return new TranslateHttpLoader();
    }
    return new ServerSafeTranslateLoader();
}

const translateConfig: TranslateModuleConfig = {
    loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [PLATFORM_ID]
    },
    isolate: false,
    extend: false
};

/**
 *
 */
export function countdownConfigFactory(): CountdownConfig {
    return { format: `mm:ss`, leftTime: GlobalParameters.CART_REMAINING_TIME_DEFAULT, notify: 0 };
}

export function getBaseHref(platformLocation: PlatformLocation): string {
    return platformLocation.getBaseHrefFromDOM();
}

@NgModule({

    declarations: [
        //Main layout
        MainLayoutComponent,
        HeaderComponent,
        FooterComponent,
        LoadingComponent,
        AccountDropdownComponent,
        CartDropdownComponent,
        EventSearchBoxComponent,
        EventCategoryMenuComponent,
        EventCategoryMobileMenuComponent,
        TopBandComponent,
        EventSearchNotFoundComponent,
        SideNavComponent,

        //Wide layout
        WideLayoutComponent,
        WideHeaderComponent,
        WideTopBandComponent,
        WideEventCategoryMenuComponent,
        WideEventCategoryMobileMenuComponent,
        WideEventSearchBoxComponent,
        WideEventSearchNotFoundComponent,
        WideAccountDropdownComponent,
        WideRegionSelectorComponent,
        WideCartDropdownComponent,
        WideFooterComponent,
        BtxDateRangePickerDialogComponent,


        //Secondary layout
        SecondaryLayoutComponent,
        SecondaryEmptyHeaderComponent,
        SecondaryEmptyFooterComponent,

        //third layout
        ThirdLayoutComponent,
        ThirdEmptyHeaderComponent,
        ThirdEmptyFooterComponent,

        //Auth layout
        AuthLayoutComponent,
        SiteLogoComponent,
        FooterToggleDirective,
        PageNotFoundComponent,

        HeaderCountdownTimerComponent,
    ],
    imports: [
        //vendor
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule,
        ReactiveFormsModule,
        FormsModule,
        FlexLayoutModule,
        MaterialModule,
        CountdownComponent,
        QRCodeComponent,
        //ngx-mask
        NgxMaskDirective,
        NgxMaskPipe,
        //ngx-translate
        TranslateModule.forRoot(translateConfig)
    ],

    exports: [

        //Main layout
        MainLayoutComponent,
        //Wide layout
        WideLayoutComponent,
        //Secondary layout
        SecondaryLayoutComponent,
        SecondaryEmptyHeaderComponent,
        SecondaryEmptyFooterComponent,
        ThirdLayoutComponent,
        ThirdEmptyHeaderComponent,
        ThirdEmptyFooterComponent,
        //Auth layout
        AuthLayoutComponent,
        SiteLogoComponent,
        PageNotFoundComponent,
        HeaderCountdownTimerComponent

    ],

    providers: [

        Title,

        //local
        HTTP_INTERCEPTORS_PROVIDERS,

        LOCAL_STORAGE_PROVIDER,

        WINDOW_PROVIDER,

        ...provideNgxMask(maskConfig),

        // HttpClient with fetch API for SSR
      provideHttpClient(withFetch(), withInterceptorsFromDi()),

        {
            provide: TRANSLATE_HTTP_LOADER_CONFIG,
            useValue: translateHttpLoaderConfig
        },

        {
            provide: APP_BASE_HREF,
            useFactory: getBaseHref,
            deps: [PlatformLocation]
        },

        StatusCodeResponseService,

        PerformancePageStateService,

        //location go back forward
        Location,
        {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        },

        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        {
            provide: MatPaginatorIntl,
            deps: [TranslateService],
            useClass: CustomPaginatorIntl
        },


        //set locale id
        I18nService,
        {
            provide: DEFAULT_LOCALE_VALUE,
            useValue: 'tr'
        },
        {
            provide: DEFAULT_LOCALES,
            useValue: [
                { lang: 'tr', displayName: 'Türkçe', flag: 'tr.svg' },
                { lang: 'en', displayName: 'English', flag: 'en.svg' }
            ]
        },

        //set region
        RegionService,
        { provide: DEFAULT_REGION_VALUE, useValue: { name: 'TURKIYE', displayName: "main.region.turkey" } },
        {
            provide: USE_DEFAULT_REGIONS, useValue: [
                { name: 'TURKIYE', displayName: "main.region.turkey" },
                { name: 'ISTANBUL', displayName: "main.region.marmara" },
                { name: 'ANKARA', displayName: "main.region.centralAnatolia" },
                { name: 'IZMIR', displayName: "main.region.aegean" },
                { name: 'ANTALYA', displayName: "main.region.mediterranean" },
                { name: 'DIGER', displayName: "main.region.other" }
            ]
        },

        //mat date picker settings
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'tr'
        },
        DatePipe,
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        {
            provide: MAT_DATE_FORMATS, useValue: {
                parse: { dateInput: 'DD/MM/YYYY' },
                display: {
                    dateInput: 'DD/MM/YYYY',
                    monthYearLabel: 'MMMM YYYY',
                    dateA11yLabel: 'DD/MM/YYYY',
                    monthYearA11yLabel: 'MMMM YYYY',
                },
            }
        },

        //count down config
        { provide: COUNTDOWN_CONFIG, useFactory: countdownConfigFactory },

        //
        LoadingScreenService,
        SetLoadingConfigGuard,
        {
            provide: LOADING_CONFIG_DEFAULT,
            useValue: { message: 'shared.labels.loadingDefaultMessage', icon: 'default.svg' }
        },

        //local services & common service
        EventService,
        EventPackageGroupService,
        FootballService,
        AuthenticationService,
        LocalStorageService,
        NotificationService,
        ConsoleLogger,
        GlobalErrorHandler,
        LanguageGuard,
        RegionGuard,
        ShoppingCartGuard,
        AuthenticationGuard,
        AnonymousGuard,
        OverlayService,
        EventAnonimUserProtectGuard,
        TFFFanService,
        TffMatchService,
        TFFStructureService,
        TFFTicketService,
        TffConstantService,

        CustomerAccountService,
        ApplicationStateService,
        TicketSaleService,
        CreditCardTypeService,
        DeliveryService,
        CustomerService,
        CustomerVerifyService,
        ShoppingCartResolver,
        LoggedInCustomerResolver,
        FanRegisterResolver,
        ReservationTicketSaleService,
        NavigationService,
        RouterService,
        CartTimeoutService,
        CartResetTimeoutResolver,
        FacebookPixelService
    ]


})

export class CoreModule {


    constructor(@Optional() @SkipSelf() coreModule: CoreModule, private title: Title) {
        if (coreModule) {
            throw new Error(`CoreModule has already been loaded. Import Core modules in the AppModule only.`);
        }
    }
}
